// Local Imports
import { graphics, schdata as s, Bbox, layout } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import * as style from "../../style";
import { EntityKind } from "../../entity";
import { DrawTo } from "../../draw";
import { Entity } from "../../tree";
import { type MousePos } from "../../mousepos";

const styleTable: style.StyleTable = {
  shape: {
    //       light light    dark  dark
    //       no    hilited  no    hilited
    fill: style.Color.new(255, 0, 0, 0.1),
    stroke: style.Color.red,
    linewidth: 2,
    cap: "round",
    join: "round",
  },
  text: {
    family: style.THE_FONT,
    style: "normal",
    weight: 700, // Typical value for "bold"
    size: 255,
    fill: style.Color.red,
    stroke: style.Special.None,
  },
};

export class PinEntity extends Entity<layout.LayoutPin> {
  readonly entityKind = EntityKind.LayoutPin;
  override readonly cache = this.makeCache<style.Style>(styleTable);

  // Where the pin is grabbed to move it: the centre of its shapes' box.
  get anchor(): s.Point {
    return layout.pinAnchor(this.data);
  }
  // The pin's shapes, moved so its anchor lands on `to`.
  shapesAnchoredAt(to: s.Point): schema.layout.LayoutPin["shapes"] {
    const from = this.anchor;
    return layout.translatePinShapes(this.data.shapes, {
      x: to.x - from.x,
      y: to.y - from.y,
    });
  }
  get style(): style.Style {
    return this.cache.style;
  }
  // Produce the `data` for our pin-name label, at the pin's anchor.
  readonly labelData = (): graphics.Text & {
    labelKind: s.ExprKind;
  } => {
    return {
      ...graphics.Text.new({
        text: this.data.name, // Pin name
        loc: this.anchor, // Location at center
        alignment: "center",
        baseline: "middle",
        rotation: 0,
        height: 2000,
        hidden: false,
      }),
      labelKind: s.ExprKind.PortName,
    };
  };

  // The box the pin's shapes cover.
  readonly bbox = (): s.Bbox => layout.pinBbox(this.data);

  // Hit test: a pin is hit only on one of its shapes, so a pin whose shapes
  // span the cell doesn't take clicks meant for instances inside it. A point
  // shape's PIN_SIZE box is tiny at typical zoom levels, so
  // `pinShapeContains` expands it.
  hitTest = (mousePos: MousePos): boolean => {
    const p = mousePos.sch;
    return this.data.shapes.some((s) => pinShapeContains(s.shape, p));
  };

  // Selection highlight: yellow ring
  private static readonly HIGHLIGHT_STYLE: style.ShapeStyle = {
    cap: "round",
    join: "round",
    linewidth: 3,
    fill: style.Special.None,
    stroke: style.Color.new(255, 255, 0, 0.9),
  };

  // Hover highlight: white ring (dimmer than selection)
  private static readonly HOVER_STYLE: style.ShapeStyle = {
    cap: "round",
    join: "round",
    linewidth: 2,
    fill: style.Special.None,
    stroke: style.Color.new(255, 255, 255, 1.0),
  };

  // Scale factor for the highlight/hover circle relative to the pin bbox.
  private static readonly HIGHLIGHT_SCALE = 1.25;

  // Hover state (separate from selection highlight)
  hovered = false;

  // Helper: draw a circle ring around the pin at the given scale.
  private drawRing = (to: DrawTo, ringStyle: style.ShapeStyle): void => {
    const bb = this.bbox();
    const cx = (bb.min.x + bb.max.x) / 2;
    const cy = (bb.min.y + bb.max.y) / 2;
    const w = bb.max.x - bb.min.x;
    const h = bb.max.y - bb.min.y;
    const sc = PinEntity.HIGHLIGHT_SCALE;
    const ringBbox = {
      min: { x: cx - (w * sc) / 2, y: cy - (h * sc) / 2 },
      max: { x: cx + (w * sc) / 2, y: cy + (h * sc) / 2 },
    };
    to.ellipse(
      graphics.Ellipse.fromPartial({ bbox: ringBbox, filled: false }),
      {
        ...ringStyle,
        linewidth: ringStyle.linewidth / to.scale,
      }
    );
  };

  override redraw = (to: DrawTo): unknown => {
    // Skip drawing if pins are globally hidden via the display options
    if (!to.layoutDisplay?.pins) return null;
    for (const pinShape of this.data.shapes) {
      to.shape(layout.pinShapeGraphic(pinShape.shape), this.style.shape);
    }
    if (to.layoutDisplay?.pinLabels) {
      to.text(this.labelData(), this.style.text, this);
    }
    // Selection highlight takes priority over hover highlight
    if (this.highlighted) {
      this.drawRing(to, PinEntity.HIGHLIGHT_STYLE);
    } else if (this.hovered) {
      this.drawRing(to, PinEntity.HOVER_STYLE);
    }
    return null;
  };
}

const POINT_HIT_SCALE = 3;

/**
 * Whether `p` lies on a pin shape: in a rect, in a polygon (even-odd), in a
 * path segment's box, or near a point (POINT_HIT_SCALE × its PIN_SIZE box).
 */
export function pinShapeContains(
  shape: schema.layout.PinShape,
  p: s.Point
): boolean {
  switch (shape.kind) {
    case "rect":
      return Bbox.contains(shape.bbox, p);
    case "polygon": {
      const pts = shape.points;
      let inside = false;
      for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const [a, b] = [pts[i], pts[j]];
        if (
          a.y > p.y !== b.y > p.y &&
          p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
        ) {
          inside = !inside;
        }
      }
      return inside;
    }
    case "path": {
      const { width, beginExt, endExt } = shape.style;
      const d = Math.max(width / 2, beginExt, endExt);
      const pts = shape.points;
      return pts.some((a, i) => {
        const box = Bbox.fromPoints(a, pts[Math.min(i + 1, pts.length - 1)]);
        return Bbox.contains(
          {
            min: { x: box.min.x - d, y: box.min.y - d },
            max: { x: box.max.x + d, y: box.max.y + d },
          },
          p
        );
      });
    }
    case "point": {
      const { width, height } = layout.PIN_SIZE;
      return Bbox.contains(
        Bbox.fromSizeAndCenter(
          { width: width * POINT_HIT_SCALE, height: height * POINT_HIT_SCALE },
          shape.at
        ),
        p
      );
    }
  }
}
